import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight, Search } from "lucide-react";
import Layout from "../components/Layout";
import { Pill, PillKind } from "../components/ui/pill";
import { Button } from "../components/ui/button";
import { FilterDropdown } from "../components/ui/filter-dropdown";
import { DataTable, DataTableColumn } from "../components/ui/data-table";
import { useEmailJobs } from "../hooks/queries";
import { EmailJob } from "../api/api";

const LIMIT = 25;

const STATUS_PILL: Record<EmailJob["status"], PillKind> = {
  sent: "health-ok",
  failed: "health-error",
  pending: "health-unconfigured",
};

const STATUS_LABEL: Record<EmailJob["status"], string> = {
  sent: "Verzonden",
  failed: "Mislukt",
  pending: "In behandeling",
};

const TYPE_FILTER = [
  { value: "alle", label: "Alle types" },
  { value: "new_vacature", label: "Nieuwe vacature" },
  { value: "stale_vacature", label: "Verouderde vacature" },
  { value: "status_change", label: "Statuswijziging" },
];

const STATUS_FILTER = [
  { value: "alle", label: "Alle statussen" },
  { value: "sent", label: "Verzonden" },
  { value: "failed", label: "Mislukt" },
  { value: "pending", label: "In behandeling" },
];

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("nl-NL", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

const COLUMNS: DataTableColumn<EmailJob>[] = [
  {
    key: "recipient", header: "Ontvanger", width: "minmax(220px,1.6fr)",
    cell: r => (
      <div className="min-w-0">
        <div className="truncate font-bold">{r.recipient_name || r.recipient_email}</div>
        <div className="truncate text-[13px] text-waves-muted">{r.recipient_email}</div>
      </div>
    ),
  },
  {
    key: "type", header: "Type", width: "minmax(130px,1fr)",
    cell: r => <span className="text-sm text-waves-body">{r.email_type.replace(/_/g, " ")}</span>,
  },
  {
    key: "status", header: "Status", width: "140px",
    cell: r => <Pill kind={STATUS_PILL[r.status]}>{STATUS_LABEL[r.status]}</Pill>,
  },
  {
    key: "when", header: "Verzonden / gepland", width: "170px",
    cell: r => <span className="whitespace-nowrap font-mono text-[13px] tabular-nums text-waves-body">{formatTimestamp(r.sent_at ?? r.scheduled_for)}</span>,
  },
  {
    key: "attempts", header: "Pogingen", width: "90px",
    cell: r => <span className="font-mono text-sm tabular-nums text-waves-body">{r.attempts}</span>,
  },
  {
    key: "error", header: "Laatste fout", width: "minmax(160px,1.2fr)",
    cell: r => <span className="block truncate text-[13px] text-waves-muted" title={r.last_error}>{r.last_error || "—"}</span>,
  },
];

export default function SysteemstatusEmails() {
  const [page, setPage] = useState(1);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [emailType, setEmailType] = useState("alle");
  const [status, setStatus] = useState("alle");

  const { data, isLoading } = useEmailJobs(page, LIMIT, {
    recipientEmail: recipientEmail || undefined,
    emailType: emailType !== "alle" ? emailType : undefined,
    status: status !== "alle" ? status : undefined,
  });

  useEffect(() => { setPage(1); }, [recipientEmail, emailType, status]);

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <Layout
      title="E-mails"
      subtitle={`Verzonden, mislukte en geplande e-mails · ${total.toLocaleString("nl-NL")} events`}
    >
      <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex h-10 w-full items-center gap-2 rounded-pill border border-waves-line bg-white px-4 sm:w-[260px]">
            <Search size={15} className="shrink-0 text-waves-muted" />
            <input
              value={recipientEmail}
              onChange={e => setRecipientEmail(e.target.value)}
              placeholder="Zoek op ontvanger…"
              className="min-w-0 flex-1 bg-transparent text-sm text-waves-ink outline-none placeholder:text-waves-muted"
            />
          </div>
          <FilterDropdown aria-label="Type" value={emailType} onChange={setEmailType} options={TYPE_FILTER} />
          <FilterDropdown aria-label="Status" value={status} onChange={setStatus} options={STATUS_FILTER} />
          <div className="ml-auto font-mono text-[13px] text-waves-muted">{rows.length} rijen</div>
        </div>

        <DataTable
          columns={COLUMNS}
          rows={rows}
          rowKey={r => r.id}
          minWidth={980}
          empty={isLoading ? "Laden…" : "Geen e-mails gevonden."}
        />

        <div className="flex items-center justify-between">
          <span className="font-mono text-[13px] text-waves-muted">
            {total === 0 ? 0 : (page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} van {total.toLocaleString("nl-NL")}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
              <ChevronLeft /> Vorige
            </Button>
            <span className="px-2 font-mono text-[13px] text-waves-body">{page} / {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
              Volgende <ChevronRight />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
